import { formatClockTime, mergeAdjacentTimeRanges } from "@thalamos/common";

import type { AvailabilityGridColumn } from "./AvailabilityGrid.js";
import type { AvailabilityBlock } from "./availabilityGridModel.js";

/** One line of a grid summary: a column that has some availability. */
export interface AvailabilityColumnSummary {
  key: string;
  /** The column's heading. */
  label: string;
  /** Earliest start, as {@link formatClockTime} writes it. */
  start: string;
  /** Latest end, as {@link formatClockTime} writes it. */
  end: string;
  /** Distinct slots within the span, once touching ones are merged. */
  slotCount: number;
}

/**
 * One line per column with availability, in column order: the column's label,
 * the earliest start, the latest end, and how many slots lie between them.
 * Slots that touch count as one, so the number matches what the grid draws
 * rather than how the blocks were built.
 */
export function summariseAvailabilityByColumn(
  columns: readonly AvailabilityGridColumn[],
  blocks: readonly AvailabilityBlock[],
): AvailabilityColumnSummary[] {
  return columns.flatMap((column, columnIndex) => {
    const slots = mergeAdjacentTimeRanges(
      blocks.filter((block) => block.columnIndex === columnIndex),
    );
    const first = slots[0];
    const last = slots.at(-1);
    if (first === undefined || last === undefined) {
      return [];
    }
    return [
      {
        key: column.key,
        label: column.label,
        start: formatClockTime(first.from),
        end: formatClockTime(last.until),
        slotCount: slots.length,
      },
    ];
  });
}
